import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Flex,
  FormField,
  Grid,
  Icon,
  Tooltip,
  Type,
} from "mesh-component-library";
import { QuestionCircleGlyph } from "mesh-icon-library";
import { useLocale } from "../../contexts/LocaleContext";
import { VINData } from "../../reducers/reducer-license-plate-vin-lookup";
import { TRANSLATION_CONSTANTS } from "../../app-consts/translations";
import FormValidators from "../../utilities/form-validators";
import {generatePath} from "../../utilities/RedirectUtils";
import {useHistory} from "react-router-dom";
import { VEHICLE_NOT_FOUND_PLATE } from "../../app-consts/errorMessageConstants";
import {
  ResponseRecords
} from "../../screens/AddVehicle/AddVehicleByLicensePlateVin/AddVehicleTypes";
import { validateVin } from "../../utils";

export type VinLookupProps = {
  searchResults?: ResponseRecords[];
  changeLookupTypeMethod: (string: "License Plate") => void;
  searchByVIN: (payload: VINData) => void;
  setVin: (payload: VINData) => void;
  partnerCode: string;
  resetSearchResults: () => void;
};

export const VinLookup = ({
  searchResults,
  changeLookupTypeMethod,
  searchByVIN,
  setVin,
  resetSearchResults,
  partnerCode,
}: VinLookupProps) => {
  const [input, setInput] = useState<string>("");
  const [hasError, setHasError] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [showSearchError, setShowSearchError] = useState<boolean>(false);
  const getTranslatedText = useLocale();
  const history = useHistory();

  useEffect(() => {
    if (searchResults === null) {
      setShowSearchError(true);
    }
  }, [searchResults]);

  useEffect(() => {
    if (validateVin(input)) {
      setHasError(false);
      setIsButtonDisabled(false);
    } else {
      setIsButtonDisabled(true);
    }
  }, [input]);

  const onBlur = () => {
    if (validateVin(input)) {
      setHasError(false);
    } else {
      setHasError(true);
    }
  };

  const onChange = (e) => {
    const formattedValue = e.target.value.toUpperCase();
    setInput(formattedValue);
  };

  const onKeyDown = (e) => {
    const isAlphaNumericVal = FormValidators.allowOnlyAlphaNumericInput(e);
    setHasError(!isAlphaNumericVal);
  };

  const onSubmit = () => {
    history.push(generatePath("vehicle-search"));

    searchByVIN({ VIN: input, partnerCode: partnerCode });
    setVin({ VIN: input, partnerCode: partnerCode });
  }

  const renderLabel = () => (
      <Flex alignItems="stretch" justifyContent="between" isBlock={true}>
        <Type size="lg" weight="bold" emphasis="information">
          {getTranslatedText(TRANSLATION_CONSTANTS.VIN)}
        </Type>
        <Tooltip
          id="vin-tooltip"
          size="lg"
          text={getTranslatedText(TRANSLATION_CONSTANTS.VIN_FIND_INSTRUCTIONS)}
        >
          <Icon
            icon={QuestionCircleGlyph}
            color="#0033A0"
            size={1.2}
            data-testid="vin-tooltip"
          />
        </Tooltip>
      </Flex>
  );

  const handleChangeToLicenseSearch = () => {
    changeLookupTypeMethod("License Plate");
    resetSearchResults();
  };

  return (
    <Grid utils={{ vrBottom: "md" }}>
      {showSearchError && (
        <Alert
          title={VEHICLE_NOT_FOUND_PLATE}
          variant="critical"
        />
      )}
      <Grid.Column>
        <FormField
          size="md"
          id="vin"
          name="VIN"
          label={renderLabel()}
          labelProps={{
            style: {
              width: "100%",
            },
          }}
          value={input}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          utils={{
            vrTop: "xs",
            vrBottom: "md",
          }}
          errorText={getTranslatedText(TRANSLATION_CONSTANTS.ENTER_VALID_VIN)}
          hasError={hasError}
          maxLength={17}
          required
          data-testid="vin-input"
        />
      </Grid.Column>
      <Grid.Column>
        <Button
          size="lg"
          hasUpgrade
          id="lookup-continue"
          data-testid="vin-lookup-continue-button"
          disabled={isButtonDisabled}
          onClick={onSubmit}
          utils={{ fullWidth: true, vrBottom: "md" }}
        >
          {getTranslatedText(TRANSLATION_CONSTANTS.CONTINUE)}
        </Button>
        <Grid.Column>
          <Type element="span" size="4xl" variant="darker" weight="normal">
            - {getTranslatedText(TRANSLATION_CONSTANTS.OR)} -
          </Type>
        </Grid.Column>
        <Grid.Column>
          <Type
            element="span"
            size="lg"
            variant="information"
            weight="semibold"
            onClick={handleChangeToLicenseSearch}
          >
            {getTranslatedText(TRANSLATION_CONSTANTS.SEARCH_BY_LICENSE_PLATE)}
          </Type>
        </Grid.Column>
      </Grid.Column>
    </Grid>
  );
};
